import { UserRole } from '../types';

/**
 * Role checking utilities that handle both string and array role formats.
 * The backend may return roles as either a single string or an array depending
 * on JWT claim parsing.
 */

/**
 * Check if a user has a specific role.
 * Handles both string and array role formats.
 *
 * @param userRole - The role from the user object (string or array)
 * @param roleToCheck - The role to check for
 * @returns true if the user has the specified role
 */
export function hasRole(
  userRole: UserRole | UserRole[] | undefined | null,
  roleToCheck: UserRole
): boolean {
  if (!userRole) return false;

  if (Array.isArray(userRole)) {
    return userRole.includes(roleToCheck);
  }

  return userRole === roleToCheck;
}

/**
 * Check if user is an Admin
 */
export function isAdmin(userRole: UserRole | UserRole[] | undefined | null): boolean {
  return hasRole(userRole, 'Admin');
}

/**
 * Check if user is a ShopOwner
 */
export function isShopOwner(userRole: UserRole | UserRole[] | undefined | null): boolean {
  return hasRole(userRole, 'ShopOwner');
}

/**
 * Check if user is a Barber
 */
export function isBarber(userRole: UserRole | UserRole[] | undefined | null): boolean {
  return hasRole(userRole, 'Barber');
}

/**
 * Check if user is a Customer
 */
export function isCustomer(userRole: UserRole | UserRole[] | undefined | null): boolean {
  return hasRole(userRole, 'Customer');
}

/**
 * Get the primary role as a string (first role if array)
 */
export function getPrimaryRole(userRole: UserRole | UserRole[] | undefined | null): UserRole | undefined {
  if (!userRole) return undefined;

  if (Array.isArray(userRole)) {
    return userRole[0];
  }

  return userRole;
}
